import { useSyncExternalStore } from 'react';

/** Device class the editor lays itself out for (spec §13, P11 Ruling 9). */
export type LayoutClass = 'phone' | 'tablet' | 'desktop';

/** Touches starting this close to a side edge belong to the system (iOS back swipe, Android back gesture). */
export const EDGE_GUTTER_PX = 16;

/** A coarse screen whose short side is under this is a phone, also turned sideways (P15: 863 × 360 is not a tablet). */
const PHONE_SHORT_SIDE_PX = 500;

/** Phone under 640 px or a coarse screen under 500 px high; tablet from 640 to 1199 px with a coarse pointer; desktop otherwise. */
export function layoutOf(width: number, coarse: boolean, height = Infinity): LayoutClass {
  if (width < 640 || (coarse && height < PHONE_SHORT_SIDE_PX)) return 'phone';
  return coarse && width < 1200 ? 'tablet' : 'desktop';
}

const coarseQuery = () => (typeof window.matchMedia === 'function' ? window.matchMedia('(pointer: coarse)') : null);

const current = (): LayoutClass => layoutOf(window.innerWidth, coarseQuery()?.matches ?? false, window.innerHeight);

function subscribe(cb: () => void): () => void {
  const q = coarseQuery();
  window.addEventListener('resize', cb);
  q?.addEventListener?.('change', cb);
  return () => {
    window.removeEventListener('resize', cb);
    q?.removeEventListener?.('change', cb);
  };
}

/** The current layout class, updated on resize and when the primary pointer changes (keyboard attached, etc.). */
export function useLayout(): LayoutClass {
  return useSyncExternalStore(subscribe, current, () => 'desktop');
}

/** Whether the primary pointer is a finger, read once (the editor's mobile gestures are decided when it opens). */
export const isCoarsePointer = (): boolean => coarseQuery()?.matches ?? false;

/** Whether the primary pointer is a finger (controls are 44 px then, spec §13). */
export function useCoarsePointer(): boolean {
  return useSyncExternalStore(subscribe, () => coarseQuery()?.matches ?? false, () => false);
}
